import { deepClone } from '../util/deepClone';

import Axis from '../Axis';
import Labeler from '../Labeler';
import subpixel from '../util/canvasSubpixelOffset';

/** A wrapper for the collection of scales and label related functions */
class AxisDate extends Axis {
	constructor(panel, scaleParams) {
		super(panel, scaleParams);
		/** @type {Labeler} */
		this.labeler = new Labeler(this, this.core);
	}

	/**
	 * Trim time labels to frame, render labels and grid
	 * @param {object} dateLabels - object with years and months to display
	 */
	render(dateLabels) {
		const { core, panel } = this;
		const { params } = core;

		// Render labels if there is room
		if (panel.size.padding.bottom > 0) {
			// Deep clone dateLabels to prevent mutations in the original object
			this.labeler.render(deepClone(dateLabels) || {});
		}

		// Normalized charts can style the normalizedate line differently
		if (
			params.normalizeDate &&
			panel.isNormalized &&
			panel.params.style.grid.vertical.normalize.color &&
			!core.dataXHR
		) {
			let index = 0;
			const normalizeDate = new Date(params.normalizeDate);

			index = core.closestExchangeIndex(normalizeDate);

			let x0 = this.primaryScale(index);
			const padLeft = panel.size.padding.left;
			const ctx = panel.rootContext;

			// Render vertical normalize line
			if (x0 && x0 > padLeft && x0 < panel.size.width + padLeft) {
				const style = panel.params.style.grid.vertical.normalize;
				const lineDash = style.lineDash;
				x0 = subpixel.px(x0);
				ctx.beginPath();
				ctx.strokeStyle = style.color;
				ctx.lineWidth = style.width;
				if (lineDash) ctx.setLineDash(lineDash.split(/\s*,\s*/));
				ctx.moveTo(x0, panel.size.padding.top);
				ctx.lineTo(x0, panel.size.padding.top + panel.size.height);
				ctx.stroke();
				ctx.setLineDash([]);
			}
		}
	}

	/**
	 * Return true if this panel gets an XAxis based on various chart settings
	 * @returns {boolean}
	 */
	hasAxis() {
		const { core, panel } = this;
		const { panelXAxis } = core.params;
		const idx = panel.index;
		const lastIdx = core.panels.length - 1;

		if (typeof panel.params.hasXAxis === 'boolean') {
			// a per-panel param overrides the global rule
			return panel.params.hasXAxis;
		} else if (panelXAxis === 'none') {
			return false;
		} else if (panelXAxis === 'all') {
			return true;
		} else if (panelXAxis === 'first' && idx === 0) {
			return true;
		} else if (panelXAxis === 'last' && idx === lastIdx) {
			return true;
		}

		return false;
	}

	/**
	 * Generates an array of dates based on a custom dataset, similar to getExchangeRuler
	 * @returns {array}
	 */
	getCustomRuler(customData = this.core.getFirstCustomDataset(this.core.data)) {
		const rulerDates = [];
		// coerce dates in case the input data is not in date format
		customData.forEach((row, i) => {
			rulerDates[i] = new Date(customData[i].date);
		});

		return rulerDates;
	}

	/**
	 * Get array of dates for an exchange, based on information from the TimeService API call.
	 * @returns {array}
	 */
	getExchangeRuler() {
		const { core } = this;
		const { tradingDays } = core.timeService;
		let i = 0;
		let iLen = 0;
		const data = [];
		let exchangeId = '';

		if (!core.state.isIntraday) {
			// interday is simple - just use the tradingdays from the timeservice call

			// convert to utc date objects
			for (i = 0, iLen = tradingDays.length; i < iLen; i++) {
				data[i] = core.getUTCDate(new Date(tradingDays[i])).getTime();
			}
		} else if (core.timeService.typicalSessions) {
			// if intraday, loop over tradingdays, and within each trading day, loop over the
			// typical session(s) for that day of the week.
			// for each trading day, it starts at the minimum session date and counts up by one minute,
			// each time checking if that minute falls within one of that day's sessions.
			// it stops when the minutes exceed the maximum session date.
			// the maximum session date may be shorter than a typical day if part of the alternatesession collection (eg partial holiday).

			const typicalSessions = [...core.timeService.typicalSessions];
			let typicalDay = {};
			let tradingDate = new Date();
			let dateMin = new Date();
			let dateMax = new Date();
			let dateMinMs = 0;
			let dateMaxMs = 0;

			for (i = 0, iLen = tradingDays.length; i < iLen; i++) {
				tradingDate = core.getUTCDate(new Date(tradingDays[i]));

				// grab typical sessions for this day.
				typicalDay = typicalSessions[tradingDate.getDay()];

				if (!typicalDay.sessions.length) {
					continue;
				}

				// convert the open/close offsets into ms
				Object.keys(typicalDay.rulerSessions).forEach(exchange => {
					const exchangeSession = typicalDay.rulerSessions[exchange];
					exchangeSession.openMs = this.getSessionDateMS(
						tradingDate,
						exchangeSession.open
					);
					exchangeSession.closeMs = this.getSessionDateMS(
						tradingDate,
						exchangeSession.close
					);
				});

				// snap the loop start/end to min/max of all sessions on tradingDate
				dateMin = this.getMinSessionDate(tradingDate, typicalDay);
				dateMax = this.getMaxSessionDate(
					tradingDays[i],
					tradingDate,
					typicalDay
				);

				// convert dates to ints for better performance
				dateMinMs = dateMin.getTime();
				dateMaxMs = dateMax.getTime();

				// collect all valid trading minutes within range
				while (dateMinMs < dateMaxMs) {
					// check if this minute falls within any session
					for (exchangeId in typicalDay.rulerSessions) {
						if (
							Object.prototype.hasOwnProperty.call(typicalDay.rulerSessions, exchangeId)
						) {
							const exchangeSession =
								typicalDay.rulerSessions[exchangeId];

							if (
								dateMinMs >= exchangeSession.openMs &&
								dateMinMs < exchangeSession.closeMs &&
								(!data.length ||
									dateMinMs > data[data.length - 1])
							) {
								data[data.length] = dateMinMs;
								break;
							}
						}
					}

					// increment one minute
					dateMinMs += 60000;
				}
			}
		}

		return data;
	}

	/**
	 * Return a date object with specified minute offset from midnight
	 * @param {Date} date - the date to be offset
	 * @param {number} minutes - how many minutes to offset from midnight
	 * @returns {Date}
	 * @private
	 */
	getSessionDateMS(date, minutes) {
		return new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate(),
			0,
			minutes
		).getTime();
	}

	/**
	 * Return a date object for a specific date with a minute offset for that day's typical session open
	 * @param {Date} tradingDate - the date to be offset
	 * @param {object} typicalDay - object with typical session open offset
	 * @property {number} typicalDay.min - number of minutes to offset open
	 * @private
	 */
	getMinSessionDate(tradingDate, typicalDay) {
		const dateMin = new Date(tradingDate);
		dateMin.setMinutes(typicalDay.min);

		return dateMin;
	}

	/**
	 * Return a date object for a specific date with a minute offset for that day's typical session close.
	 * Also accounts for alternate session information
	 * @param {Date} tradingDay
	 * @param {Date} tradingDate - the date to be offset
	 * @param {object} typicalDay - object with typical session close offset
	 * @property {number} typicalDay.max - number of minutes to offset close
	 * @returns {Date}
	 * @private
	 */
	getMaxSessionDate(tradingDay, tradingDate, typicalDay) {
		let dateMin = new Date(tradingDate);
		const { core } = this;

		// default to typical day's max
		dateMin.setMinutes(typicalDay.max);

		// adjust for alternate sessions
		if (core.params.collapseAlternateSessionGaps) {
			const alternateSessions = core.timeService.alternateSessions || {};

			// find alternate session on this trading date
			Object.keys(alternateSessions).forEach(key => {
				if (key === tradingDay.replace(/Z$/, '')) {
					// this day's date matches alternatesessions date

					// calculate new max
					let alternateMax = 0;

					// loop through typical day's rulersessions
					Object.keys(typicalDay.rulerSessions).forEach(exchangeId => {
						// alternate sessions for this exchange
						const matches = alternateSessions[key].filter(row => row.exchangeId === exchangeId);

						// if alternate session found, use alternate close date
						if (matches.length > 0) {
							// find max of all matches
							const matchesMax = Math.max(
								matches.map(o => o.close)
							);
							alternateMax = Math.max(alternateMax, matchesMax);
						} else {
							// if no match, use typical close date
							alternateMax = Math.max(
								alternateMax,
								typicalDay.rulerSessions[exchangeId].close
							);
						}
					});

					// set alternate dateMin for this day only
					if (alternateMax > 0) {
						dateMin = new Date(tradingDate);
						dateMin.setMinutes(alternateMax);
					}
				}
			});
		}

		return dateMin;
	}
}

export default AxisDate;
